import type { TeamPlanningSyncResult } from '@obraplan/core/application/services/team-planning-sync';
import type { WorkforceView } from '@obraplan/core/application/use-cases/workforce/workforce-use-cases';
import { PLANNING_ERROR } from '@obraplan/core/domain/planning/errors';
import { isAllocatable } from '@obraplan/core/domain/workforce/employee';
import { membershipOpenOn } from '@obraplan/core/domain/workforce/team';
import { formatShortDate } from '@/lib/format';

/**
 * Alocar a equipe (Fase 30, etapa 3 — RN-423), do lado da tela.
 *
 * O servidor decide quem recebe a alocação; a tela mostra antes quem vai receber, com a mesma
 * régua: o membro do dia é o da filiação aberta naquela data (RN-053), e só entra quem pode
 * receber alocação (RN-054) — funcionário ativo, com o recurso ativo na obra.
 */

export interface TeamMemberOption {
  readonly resourceId: string;
  readonly name: string;
}

/** Uma equipe que pode ser alocada, com quem ela aloca em cada dia da semana. */
export interface TeamAllocationTeam {
  readonly teamId: string;
  readonly name: string;
  /**
   * Quem recebe a alocação em cada dia, pela data. Ausente enquanto a mão de obra não chegou:
   * a tela não diz "ninguém" antes de saber.
   */
  readonly membersByDate?: Readonly<Record<string, readonly TeamMemberOption[]>>;
}

/** O pedido que sai do diálogo; a página põe a obra e a semana. */
export interface TeamAllocationSubmit {
  readonly teamId: string;
  readonly activityId: string;
  readonly serviceId: string;
  /** As horas são de cada pessoa (D8); a meta é da equipe no dia (RN-424). */
  readonly days: readonly {
    readonly date: string;
    readonly quarterHours: number;
    readonly quantity?: number;
  }[];
  readonly justification?: string;
  readonly acknowledgeRestriction?: boolean;
}

/** A recusa do servidor, como o diálogo a mostra. */
export interface TeamAllocationRefusal {
  readonly code: string;
  readonly message: string;
}

/** A recusa que se resolve escrevendo o motivo da hora extra (RN-045). */
export function asksForJustification(code: string): boolean {
  return code === PLANNING_ERROR.CAPACITY_OVERRIDE_NEEDS_REASON;
}

/** Quem a equipe aloca num dia — a mesma régua do `CreateTeamAllocation`. */
export function teamMembersOn(
  view: Pick<WorkforceView, 'members' | 'resources'>,
  teamId: string,
  date: string,
): TeamMemberOption[] {
  const porFuncionario = new Map<string, TeamMemberOption>();
  for (const item of view.resources) {
    const { resource, employee } = item;
    if (resource.type !== 'EMPLOYEE' || resource.employeeId === undefined) continue;
    if (!resource.isActive || resource.deletedAt !== undefined) continue;
    if (employee === undefined || !isAllocatable(employee)) continue;
    porFuncionario.set(resource.employeeId, { resourceId: resource.id, name: item.name });
  }

  const vistos = new Set<string>();
  return view.members
    .filter((member) => member.teamId === teamId && membershipOpenOn(member, date))
    .flatMap((member) => {
      if (vistos.has(member.employeeId)) return [];
      vistos.add(member.employeeId);
      const pessoa = porFuncionario.get(member.employeeId);
      return pessoa === undefined ? [] : [pessoa];
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

/** O aviso da troca de equipe sobre o planejamento (RN-426). Ausente quando nada mudou. */
export interface TeamMoveNotice {
  readonly tone: 'warning' | 'info';
  readonly message: string;
}

/** Quantos dias que não couberam o aviso nomeia; o resto vira "e mais N". */
const DIAS_NOMEADOS = 3;

/**
 * O que a troca de equipe fez no planejamento, numa frase (RN-426).
 *
 * O dia que não coube na jornada de quem entrou é o que pede atenção: ele ficou de fora, e
 * alguém precisa decidir o que fazer com ele. Sem isso, basta dizer que o planejamento
 * acompanhou — e, se nada mudou, não há o que dizer.
 */
export function teamMoveNotice(
  planning: TeamPlanningSyncResult | undefined,
  employeeName: string,
): TeamMoveNotice | undefined {
  if (planning === undefined) return undefined;
  const { skipped } = planning;
  if (skipped.length > 0) {
    const nomeados = skipped.slice(0, DIAS_NOMEADOS).map((dia) => {
      const frente = dia.activityLabel ?? 'frente fora da estrutura';
      const servico = dia.serviceName === undefined ? '' : ` · ${dia.serviceName}`;
      return `${formatShortDate(dia.date)} ${frente}${servico}`;
    });
    const resto = skipped.length - nomeados.length;
    const cabeca =
      skipped.length === 1
        ? `1 dia não coube na jornada de ${employeeName}`
        : `${String(skipped.length)} dias não couberam na jornada de ${employeeName}`;
    return {
      tone: 'warning',
      message: `${cabeca}: ${nomeados.join('; ')}${resto > 0 ? ` e mais ${String(resto)}` : ''}.`,
    };
  }
  if (planning.added + planning.removed === 0) return undefined;
  return {
    tone: 'info',
    message: `Planejamento acompanhou: +${String(planning.added)} / −${String(planning.removed)} alocações`,
  };
}

/** As equipes do diálogo: as ativas do quadro, com quem cada uma aloca em cada dia. */
export function teamAllocationTeams(
  teams: readonly { readonly teamId: string; readonly name: string; readonly isActive: boolean }[],
  dates: readonly string[],
  view: Pick<WorkforceView, 'members' | 'resources'> | undefined,
): TeamAllocationTeam[] {
  return teams
    .filter((team) => team.isActive)
    .map((team) => ({
      teamId: team.teamId,
      name: team.name,
      ...(view === undefined
        ? {}
        : {
            membersByDate: Object.fromEntries(
              dates.map((date) => [date, teamMembersOn(view, team.teamId, date)]),
            ),
          }),
    }));
}
